// Calendar dates and local wall times are distinct from instants. This module
// never relies on the server's timezone or Date's permissive string parser.
export function validatedTimeZone(value: string): string {
  if (typeof value !== 'string' || !value || value.length > 100)
    throw new Error('Choose a valid timezone, such as Europe/London.');
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: value }).resolvedOptions().timeZone;
  } catch {
    throw new Error('Choose a valid timezone, such as Europe/London.');
  }
}

function partsInZone(instant: Date, timeZone: string) {
  return Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(instant)
      .map(({ type, value }) => [type, value]),
  );
}

export function dateInZone(instant: Date, timeZone: string): string {
  const parts = partsInZone(instant, validatedTimeZone(timeZone));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function localDateTime(instant: Date, timeZone: string): string {
  const parts = partsInZone(instant, validatedTimeZone(timeZone));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function parseDuration(value: string): number {
  const match = /^(\d+)(m|h|d)$/.exec(value);
  if (!match) throw new Error('Use a duration such as 30m, 2h or 1d.');
  const duration = Number(match[1]) * { m: 60_000, h: 3_600_000, d: 86_400_000 }[match[2]]!;
  if (!Number.isSafeInteger(duration) || duration < 60_000 || duration > 366 * 86_400_000)
    throw new Error('Choose a duration between 1 minute and 366 days.');
  return duration;
}

export function resolveSchedule(value: string, timeZone: string, now = new Date()): string {
  const zone = validatedTimeZone(timeZone);
  let wall = value.trim();
  const relative = /^(today|tomorrow)\s+(\d{2}:\d{2})$/i.exec(wall);
  if (relative) {
    const day = new Date(`${dateInZone(now, zone)}T12:00:00Z`);
    if (relative[1].toLowerCase() === 'tomorrow') day.setUTCDate(day.getUTCDate() + 1);
    wall = `${day.toISOString().slice(0, 10)} ${relative[2]}`;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})$/.exec(wall);
  if (!match) throw new Error('Use "YYYY-MM-DD HH:mm", "today HH:mm" or "tomorrow HH:mm".');
  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  const calendar = Date.UTC(year, month - 1, day, hour, minute);
  const normalized = new Date(calendar).toISOString().slice(0, 16);
  if (year < 2000 || year > 2100 || normalized !== wall.replace(' ', 'T'))
    throw new Error('Enter a real calendar date and a 24-hour time between 2000 and 2100.');

  // Probe both sides of a possible clock change. Candidate offsets then undergo
  // exact round-trip comparison, so gaps and repeated wall times are rejected.
  const offsets = new Set<number>();
  for (const delta of [-48, -24, 0, 24, 48]) {
    const probe = calendar + delta * 3_600_000;
    const p = partsInZone(new Date(probe), zone);
    offsets.add(Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - probe);
  }
  const matches = [...offsets]
    .map((offset) => new Date(calendar - offset))
    .filter((candidate) => localDateTime(candidate, zone) === normalized);
  if (matches.length === 0)
    throw new Error(
      'That local time does not exist because the clocks change. Choose another time.',
    );
  if (matches.length !== 1)
    throw new Error(
      'That local time happens twice because the clocks change. Choose an unambiguous time.',
    );
  return matches[0].toISOString();
}
